import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API_BASE_URL from "./apiBase";

const GA_SCRIPT_ID = "ga4-gtag-script";
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{6,20}$/;

function disableAnalytics(measurementId) {
  if (measurementId) {
    window[`ga-disable-${measurementId}`] = true;
  }

  const script = document.getElementById(GA_SCRIPT_ID);
  if (script) {
    script.remove();
  }
}

function ensureGoogleAnalytics(measurementId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window[`ga-disable-${measurementId}`] = false;

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      measurementId
    )}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
  }
}

export default function useGoogleAnalytics() {
  const location = useLocation();
  const [measurementId, setMeasurementId] = useState("");
  const previousMeasurementIdRef = useRef("");

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_BASE_URL}/get_analytics_settings.php`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        const nextMeasurementId = String(data.measurement_id || "")
          .trim()
          .toUpperCase();

        setMeasurementId(
          data.success && MEASUREMENT_ID_PATTERN.test(nextMeasurementId)
            ? nextMeasurementId
            : ""
        );
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Google Analytics settings error:", err);
        }
        setMeasurementId("");
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const previousMeasurementId = previousMeasurementIdRef.current;

    if (previousMeasurementId && previousMeasurementId !== measurementId) {
      disableAnalytics(previousMeasurementId);
    }

    previousMeasurementIdRef.current = measurementId;

    if (!measurementId) {
      disableAnalytics(previousMeasurementId);
      return;
    }

    ensureGoogleAnalytics(measurementId);

    window.gtag("config", measurementId, {
      page_title: document.title,
      page_path: `${location.pathname}${location.search}`,
      page_location: window.location.href,
    });
  }, [measurementId, location.pathname, location.search]);
}
